var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import * as React from "react";
import { Checkbox, Dimmer, Form, Grid, Header, Loader } from 'semantic-ui-react';
import ErrorDisplay from "../../ErrorDisplay";
import { postJsonWithAuthn } from "../../RequestHelpers";
import { ChromogenTypeEnum, ProtocolTypeEnum } from "../automated-pro/EditKitForm";
import { buildOnComponentStateChangeHandler } from "../FormState";
import { defaultProbeSetState, defaultSampleTypeState, discountMessage } from "../rna-fish/EditKitForm";
import { ErrorElementsWithPrices } from "../rna-fish/NewRnaFishKitPage";
import ProbeSetForm from "../rna-fish/ProbeSetForm";
import SelectSampleTypeForm from "../rna-fish/SelectSampleTypeForm";
import BufferForm from "./BufferForm";
import { findChromogenPresetScale, findDetectPresetScale, ManualProConsumerDetectForm } from "./ManualProConsumerDetectForm";
import ManualProConsumerKitSerializer from "./ManualProConsumerKitSerializer";
import { NONE_CHROMOGEN } from "../automated-pro/RnaCishKitToAdminFormState";
export var defaultDetectState = function (catalog) {
    var _a;
    return ({
        scale: catalog.defaultManualProScale,
        enzyme: catalog.proAmplifier.manualProAmps.proAmpConfigList.find(function (a) { return a.isDefault; }),
        chromogen: (_a = catalog.proChromogens.chromogens.find(function (c) { return c.id === ChromogenTypeEnum.MATISSE_BROWN; })) !== null && _a !== void 0 ? _a : {
            id: 'NONE',
            label: 'NONE',
            platformCompatibility: [],
            enzymeCompatibility: '',
            bufferCompatibility: { id: 'NONE', label: 'NONE' }
        },
        presetDetectScale: findDetectPresetScale(catalog.presetScales, ProtocolTypeEnum.MANUAL, catalog.defaultManualProScale, 1, false),
        presetChromogenScale: findChromogenPresetScale(catalog.presetScales, ProtocolTypeEnum.MANUAL, catalog.proChromogens.chromogens.find(function (c) { return c.id === ChromogenTypeEnum.MATISSE_BROWN; }), catalog.defaultManualProScale)
    });
};
export var defaultBufferState = function (catalog) { return ({
    hybridization: catalog.manualProBuffer.hybridization.defaultVolumeMl,
    probeWash: catalog.manualProBuffer.probeWash.defaultVolumeMl,
    ampWash: catalog.manualProBuffer.ampWash.defaultVolumeMl
}); };
export var defaultFormState = function (catalog) { return ({
    detectEnabled: true,
    buffersEnabled: true,
    comment: '',
    errors: [],
    price: undefined,
    priceLoading: false,
    probeSetEnabled: true,
    detectState: defaultDetectState(catalog),
    bufferState: defaultBufferState(catalog),
    probeSetState: defaultProbeSetState(catalog, false, true),
    sampleTypeFormState: defaultSampleTypeState()
}); };
/**
 * For this to show up, make sure to wrap the EditKitForm in a Form who sets `error` when errors is non empty
 */
var EditKitForm = /** @class */ (function (_super) {
    __extends(EditKitForm, _super);
    function EditKitForm(props) {
        var _this = _super.call(this, props) || this;
        _this.onPscStateChange = buildOnComponentStateChangeHandler(pscStateWrangler, _this.props.setFormState);
        _this.onDetectStateChange = buildOnComponentStateChangeHandler(detectStateWrangler, _this.props.setFormState);
        _this.onBufferStateChange = buildOnComponentStateChangeHandler(bufStateWrangler, _this.props.setFormState);
        _this.onSampleTypeStateChangeBaseHandler = buildOnComponentStateChangeHandler(sampleTypeStateWrangler, _this.props.setFormState);
        _this.onProbeSetEnableChange = function () {
            _this.props.setFormState(function (s) {
                return { probeSetEnabled: !s.probeSetEnabled };
            }, function (newState) {
                // if probe set was just enabled, set the amplifier component's amplifier
                if (newState.probeSetEnabled) {
                    _this.onProbeSetStateChange({}, _this.props.formState.probeSetState.hcrAmplifier, _this.onUserSelection);
                }
                else {
                    _this.onUserSelection();
                }
            });
        };
        _this.onProbeSetStateChange = function (updateObjOrFn, prevAmplifier, callback) {
            var stateCalculator;
            if (typeof updateObjOrFn === "function") {
                stateCalculator = function (oldState) {
                    // apply the provided function
                    return updateObjOrFn(oldState.probeSetState);
                };
            }
            else {
                stateCalculator = function () {
                    // just return the plain object
                    return updateObjOrFn;
                };
            }
            _this.props.setFormState(function (oldState) { return ({
                probeSetState: Object.assign({}, oldState.probeSetState, stateCalculator(oldState))
            }); }, function (newState) {
                // propagate to amplifier component
                var config = _this.props.catalog.proAmplifier.manualProAmps
                    .proAmpConfigList.find(function (a) {
                    return a.amplifier.id === newState.probeSetState.hcrAmplifier.id;
                });
                if (config == null) {
                    throw Error('Invalid channel');
                }
                _this.onDetectStateChange({
                    enzyme: config
                }, callback);
            });
        };
        _this.onDetectComponentStateChange = function (updateObjOrFn, callback) {
            var stateCalculator;
            if (typeof updateObjOrFn === "function") {
                stateCalculator = function (oldState) {
                    // apply the provided function
                    return updateObjOrFn(oldState.detectState);
                };
            }
            else {
                stateCalculator = function () {
                    // just return the plain object
                    return updateObjOrFn;
                };
            }
            _this.props.setFormState(function (oldState) { return ({
                detectState: Object.assign({}, oldState.detectState, stateCalculator(oldState))
            }); }, function (newState) {
                // propagate to probe set component
                _this.onPscStateChange({
                    hcrAmplifier: newState.detectState.enzyme.amplifier
                }, callback);
            });
        };
        _this.onDetectEnableChange = function () {
            _this.props.setFormState(function (s) {
                return { detectEnabled: !s.detectEnabled };
            }, function (newState) {
                if (newState.detectEnabled) {
                    _this.onDetectStateChange({}, _this.onUserSelection);
                }
                else {
                    _this.onUserSelection();
                }
            });
        };
        _this.onBuffersEnableChange = function () {
            _this.props.setFormState(function (s) {
                return { buffersEnabled: !s.buffersEnabled };
            }, function () {
                _this.onUserSelection();
            });
        };
        _this.onCommentChange = function (event) {
            var str = event.currentTarget.value;
            _this.props.setFormState(function () {
                return {
                    comment: str
                };
            });
        };
        // Wrapper around normal state propagation to handle setting the probeWash buffer to 0 when northern blot is selected
        _this.onSampleTypeStateChange = function (sampleTypeFormState, prevSampleType, callback) {
            return _this.onSampleTypeStateChangeBaseHandler(sampleTypeFormState, function (newState) {
                var sampleType = newState.sampleTypeFormState.sampleType;
                if (sampleType.id === "WHOLE_MOUNT") {
                    _this.onDetectStateChange({
                        scale: _this.props.catalog.defaultManualProScale,
                        presetDetectScale: findDetectPresetScale(_this.props.catalog.presetScales, ProtocolTypeEnum.MANUAL, _this.props.catalog.defaultManualProScale, 1, false),
                        presetChromogenScale: findChromogenPresetScale(_this.props.catalog.presetScales, ProtocolTypeEnum.MANUAL, _this.props.formState.detectState.chromogen, _this.props.catalog.defaultManualProScale)
                    }, callback);
                }
                else if (!["TISSUE_SECTION", "CELLS_SLIDE"].includes(prevSampleType.id) &&
                    ["TISSUE_SECTION", "CELLS_SLIDE"].includes(sampleType.id)) {
                    var scale10 = _this.props.catalog.manualProScaleOptions.find(function (s) { return s === 10; });
                    _this.onDetectStateChange({
                        scale: scale10,
                        presetDetectScale: scale10 ? findDetectPresetScale(_this.props.catalog.presetScales, ProtocolTypeEnum.MANUAL, scale10, 1, sampleType.id === 'CELLS_SUSPENSION') : undefined,
                        presetChromogenScale: scale10 ? findChromogenPresetScale(_this.props.catalog.presetScales, ProtocolTypeEnum.MANUAL, _this.props.formState.detectState.chromogen, scale10) : undefined
                    }, callback);
                }
                else {
                    // The detect preset scale is Pro Flow-specific, so re-select it against the new sample type
                    // whenever the sample type changes (e.g. into or out of Cells in Suspension), even if the
                    // scale itself doesn't change.
                    _this.onDetectStateChange(function (oldState) { return ({
                        presetDetectScale: findDetectPresetScale(_this.props.catalog.presetScales, ProtocolTypeEnum.MANUAL, oldState.scale, 1, sampleType.id === 'CELLS_SUSPENSION'),
                        chromogen: sampleType.id === "CELLS_SUSPENSION" ? NONE_CHROMOGEN : oldState.chromogen,
                        presetChromogenScale: sampleType.id === "CELLS_SUSPENSION" ? undefined : oldState.presetChromogenScale
                    }); }, callback);
                }
            });
        };
        // The user finished editing something
        _this.onUserSelection = function () {
            _this.loadPrice();
        };
        _this.loadPrice = function () {
            // start by setting the state so that the callback will see the state after previous changes have applied
            _this.props.setFormState(function () { return ({ priceLoading: true }); }, function (newState) {
                var kitBody = EditKitForm.serializeManualProConsumerKit(newState);
                postJsonWithAuthn(_this.props.kitPriceUrlPath, JSON.stringify(kitBody))
                    .then(function (resp) {
                    if (resp.status === 400) {
                        return resp.json().then(function (json) { return new ErrorElementsWithPrices(json.errors, json.price); });
                    }
                    return resp.json();
                }).then(function (resp) {
                    if (resp instanceof ErrorElementsWithPrices) {
                        _this.props.setFormState(function () { return ({
                            errors: resp.errors,
                            priceLoading: false,
                            price: resp.price
                        }); });
                    }
                    else {
                        _this.props.setFormState(function () { return ({
                            errors: [],
                            price: resp,
                            priceLoading: false
                        }); });
                    }
                }).catch(function (rej) {
                    console.error("Couldn't update price: " + rej);
                });
            });
        };
        return _this;
    }
    EditKitForm.prototype.render = function () {
        var _a, _b, _c, _d;
        return React.createElement("div", { className: 'kit-form' },
            React.createElement(ErrorDisplay, { errors: this.props.formState.errors, errorPath: "/" }),
            React.createElement("div", null),
            React.createElement(Header, { className: "component-slider" },
                React.createElement("span", { className: 'sample-type-span' }, "Sample Type")),
            React.createElement("div", { className: 'no-outline-kit-component' },
                React.createElement(SelectSampleTypeForm, { sampleTypeState: this.props.formState.sampleTypeFormState, sampleTypes: this.props.catalog.manualProBuffer.sampleTypes, manualPro: true, onSampleTypeChange: this.onSampleTypeStateChange, onUserSelection: this.onUserSelection, showErrors: this.props.showErrors, errors: this.props.formState.errors })),
            React.createElement(ToggleableKitComponent, { componentEnabled: this.props.formState.probeSetEnabled, otherComponentStates: [this.props.formState.detectEnabled, this.props.formState.buffersEnabled], onChange: this.onProbeSetEnableChange, label: "HCR™ HiFi Probe", cish: false },
                React.createElement(ProbeSetForm, { catalog: this.props.catalog, errors: this.props.formState.errors, enabled: this.props.formState.probeSetEnabled, enableNegativeControlProbe: this.props.enableNegativeControlProbe, enableCustomPrice: this.props.enableCustomPrice, enableCustomScale: this.props.enableProbeSetCustomScale, onProbeSetChange: this.onProbeSetStateChange, onUserSelection: this.onUserSelection, effectivePriceCents: this.props.formState.price ? this.props.formState.price.effectivePrice.probeSetPriceCents : undefined, listPriceCents: this.props.formState.price ? this.props.formState.price.listPrice.probeSetPriceCents : undefined, priceLoading: this.props.formState.priceLoading, probeSetState: this.props.formState.probeSetState, amplifierDropdownDisabled: this.props.formState.detectEnabled, selectedSampleType: this.props.formState.sampleTypeFormState.sampleType, restrictCustomTargetProbeSetSizeToCatalog: this.props.restrictCustomTargetProbeSetSizeToCatalog, manualPro: true, legacy: false, showErrors: this.props.showErrors })),
            React.createElement(ToggleableKitComponent, { componentEnabled: this.props.formState.detectEnabled, otherComponentStates: [this.props.formState.probeSetEnabled, this.props.formState.buffersEnabled], onChange: this.onDetectEnableChange, label: "HCR™ Pro Amplifier Kit", cish: false },
                React.createElement(ManualProConsumerDetectForm, { detectState: this.props.formState.detectState, catalog: this.props.catalog, errors: this.props.formState.errors, onDetectStateChange: this.onDetectComponentStateChange, selectedSampleType: this.props.formState.sampleTypeFormState.sampleType, onUserSelection: this.onUserSelection, effectivePriceCents: this.props.formState.price ? this.props.formState.price.effectivePrice.detectPriceCents : undefined, listPriceCents: this.props.formState.price ? this.props.formState.price.listPrice.detectPriceCents : undefined, priceLoading: this.props.formState.priceLoading, showErrors: this.props.showErrors })),
            React.createElement(ToggleableKitComponent, { componentEnabled: this.props.formState.buffersEnabled, otherComponentStates: [this.props.formState.probeSetEnabled, this.props.formState.detectEnabled], onChange: this.onBuffersEnableChange, label: "HCR™ Buffers", cish: false },
                React.createElement(BufferForm, { sampleType: this.props.formState.sampleTypeFormState.sampleType, bufferState: this.props.formState.bufferState, catalog: this.props.catalog.goldFishBuffer, enableCustomPrice: this.props.enableCustomPrice, enableCustomScale: this.props.enableBufferCustomScale, errors: this.props.formState.errors, onBufferChange: this.onBufferStateChange, onUserSelection: this.onUserSelection, effectivePriceCents: this.props.formState.price ? this.props.formState.price.effectivePrice.buffersPriceCents : undefined, listPriceCents: this.props.formState.price ? this.props.formState.price.listPrice.buffersPriceCents : undefined, priceLoading: this.props.formState.priceLoading, showErrors: this.props.showErrors })),
            React.createElement(ErrorDisplay, { errors: this.props.formState.errors, errorPath: "/" }),
            React.createElement(Grid, { columns: 4, stackable: true },
                React.createElement(Grid.Row, null,
                    React.createElement(Grid.Column, { width: 12 },
                        React.createElement(Form.Field, null,
                            React.createElement("label", { htmlFor: 'comment' }, "Comment"),
                            React.createElement(Form.TextArea, { style: { fontFamily: 'Avenir Next' }, placeholder: 'Please share any instructions for probe design, ' +
                                    'order fulfillment, or other relevant details regarding this order. ' +
                                    'If you have a preferred transcriptome for probe design, ' +
                                    'let us know how we can access it.', value: this.props.formState.comment, onChange: this.onCommentChange }))),
                    priceColumn(React.createElement("div", { 
                        // apply padding that matches the kit component div padding
                        style: { 'paddingRight': '.5rem' } },
                        React.createElement(PriceDisplay, { loading: this.props.formState.priceLoading, priceCents: this.props.formState.probeSetEnabled &&
                                this.props.formState.probeSetState.negativeControlProbe &&
                                ((_b = (_a = this.props.formState.price) === null || _a === void 0 ? void 0 : _a.effectivePrice) === null || _b === void 0 ? void 0 : _b.subtotalCents) != null
                                ? this.props.formState.price.effectivePrice.subtotalCents + 10000
                                : (_d = (_c = this.props.formState.price) === null || _c === void 0 ? void 0 : _c.effectivePrice) === null || _d === void 0 ? void 0 : _d.subtotalCents })))),
                React.createElement(Grid.Row, null,
                    React.createElement(Grid.Column, { width: 16 }, this.props.formState.price && discountMessage(this.props.formState.price.discountCents, this.props.formState.price.listPrice.subtotalCents)))));
    };
    EditKitForm.prototype.componentDidMount = function () {
        if (this.props.loadPriceOnMount) {
            this.loadPrice();
        }
    };
    EditKitForm.serializeManualProConsumerKit = function (formState) {
        var serializer = new ManualProConsumerKitSerializer();
        if (formState.probeSetEnabled) {
            serializer.setProbeSetState(formState.probeSetState);
        }
        if (formState.detectEnabled) {
            serializer.setDetect(formState.detectState);
        }
        if (formState.buffersEnabled) {
            serializer.setBuffers(formState.bufferState);
        }
        return serializer.serializeKit(formState.comment, formState.sampleTypeFormState.sampleType);
    };
    return EditKitForm;
}(React.PureComponent));
export default EditKitForm;
var PriceDisplay = /** @class */ (function (_super) {
    __extends(PriceDisplay, _super);
    function PriceDisplay() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    PriceDisplay.prototype.render = function () {
        return React.createElement("div", null,
            React.createElement(Loader, { active: this.props.loading, inline: true, size: 'mini' }),
            "\u00A0 Price (USD): ",
            this.props.priceCents === undefined ? ' (pending)' : '$' +
                formatMonetaryDecimal(this.props.priceCents));
    };
    return PriceDisplay;
}(React.PureComponent));
export { PriceDisplay };
var ToggleableKitComponent = /** @class */ (function (_super) {
    __extends(ToggleableKitComponent, _super);
    function ToggleableKitComponent(props) {
        return _super.call(this, props) || this;
    }
    ToggleableKitComponent.prototype.render = function () {
        return React.createElement(React.Fragment, null,
            !this.props.cish ?
                React.createElement(Header, { className: "component-slider" },
                    React.createElement(Grid, { columns: 2, stackable: true },
                        React.createElement(Grid.Column, null,
                            React.createElement("span", null, this.props.label)),
                        React.createElement(Grid.Column, { textAlign: 'right' },
                            React.createElement(Checkbox, { checked: this.props.componentEnabled, 
                                // lock this one on if all the others are disabled
                                disabled: this.props.componentEnabled && this.props.otherComponentStates.every(function (b) { return !b; }), onChange: this.props.onChange, toggle: true })))) :
                React.createElement(Header, { className: "cish-component-slider" },
                    React.createElement(Grid, { columns: 2, stackable: true },
                        React.createElement(Grid.Row, null,
                            React.createElement(Grid.Column, null, this.props.componentEnabled && React.createElement("span", null, this.props.label)),
                            React.createElement(Grid.Column, { textAlign: 'right' },
                                React.createElement(Checkbox, { checked: this.props.componentEnabled, 
                                    // lock this one on if all the others are disabled
                                    disabled: (this.props.componentEnabled && this.props.otherComponentStates.every(function (b) { return !b; }) || this.props.disableToggle), 
                                    // zero width space label helps vertical alignment
                                    label: '\u00A0', onChange: this.props.onChange, toggle: true }))))),
            React.createElement(Dimmer.Dimmable, { dimmed: !this.props.componentEnabled, className: this.props.cish ? 'cish-admin-kit-component' : 'kit-component' },
                !this.props.cish ?
                    React.createElement(Dimmer, { active: !this.props.componentEnabled, inverted: true },
                        React.createElement(Header, null,
                            "No ",
                            this.props.label)) :
                    React.createElement(Dimmer, { className: 'dimmed-component', active: !this.props.componentEnabled, inverted: true },
                        React.createElement(Header, null,
                            "NO ",
                            this.props.label)),
                this.props.children));
    };
    return ToggleableKitComponent;
}(React.PureComponent));
export { ToggleableKitComponent };
export var priceColumn = function (priceNode) {
    return React.createElement(Grid.Column, { style: { 'position': 'relative' } },
        React.createElement("div", { style: {
                'position': 'absolute',
                'bottom': '0',
                // re-apply the padding for a Grid.Column
                'right': '1rem',
                'marginTop': '.5rem'
            } }, priceNode));
};
export var formatMonetaryDecimal = function (cents) {
    if (cents < 0) {
        return "(" + (-cents / 100).toFixed(2) + ")";
    }
    return (cents / 100).toFixed(2);
};
var PscStateWrangler = /** @class */ (function () {
    function PscStateWrangler() {
        this.buildHolderFromComponentState = function (componentFormState) { return ({ probeSetState: componentFormState }); };
        this.extractComponentState = function (holder) { return holder.probeSetState; };
    }
    return PscStateWrangler;
}());
var DetectStateWrangler = /** @class */ (function () {
    function DetectStateWrangler() {
        this.buildHolderFromComponentState = function (componentFormState) { return ({ detectState: componentFormState }); };
        this.extractComponentState = function (holder) { return holder.detectState; };
    }
    return DetectStateWrangler;
}());
var BufferStateWrangler = /** @class */ (function () {
    function BufferStateWrangler() {
        this.buildHolderFromComponentState = function (componentFormState) { return ({ bufferState: componentFormState }); };
        this.extractComponentState = function (holder) { return holder.bufferState; };
    }
    return BufferStateWrangler;
}());
var SampleTypeStateWrangler = /** @class */ (function () {
    function SampleTypeStateWrangler() {
        this.buildHolderFromComponentState = function (componentFormState) { return ({ sampleTypeFormState: componentFormState }); };
        this.extractComponentState = function (holder) { return holder.sampleTypeFormState; };
    }
    return SampleTypeStateWrangler;
}());
var pscStateWrangler = new PscStateWrangler();
var detectStateWrangler = new DetectStateWrangler();
var bufStateWrangler = new BufferStateWrangler();
var sampleTypeStateWrangler = new SampleTypeStateWrangler();
//# sourceMappingURL=ManualProConsumerKitForm.js.map


//////////////////
// WEBPACK FOOTER
// ../common/build/dist/kit/manual-pro/ManualProConsumerKitForm.js
// module id = 91
// module chunks = 0